import { everyTrue, someTrue } from 'aa_common/front-end/helpers'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { useEffect, useState } from 'react'

import { checkIsEventAfterAdjusted } from '../../helper/check-event-after-adjusted'
import { getCorrectionBlockedReason } from '../helper'

type Params = {
  biid: string
  isCorrectionMode: boolean
  isCorrectionBlockedByEventHistory: boolean
  adjusted: boolean
  isCorrectionBlockedByMonthlyClosing: boolean
  transitionAt?: string | null
  isCorrectionBlockedByAcquisitionMonthlyClosing: boolean
  acquiredAt?: string | null
  isDraftFA?: boolean
}

export const useCorrectionBlocked = ({
  biid,
  isCorrectionMode,
  isCorrectionBlockedByEventHistory,
  adjusted,
  isCorrectionBlockedByMonthlyClosing,
  transitionAt,
  isCorrectionBlockedByAcquisitionMonthlyClosing,
  acquiredAt,
  isDraftFA,
}: Params) => {
  const { isEnable: isCorrectionBlockedReasonEnabled } = useCheckFlag(FeatureFlagNames.AssetCorrectionBlockedReason)

  // Draft fixed assets can be edited with formMode=CORRECTION too, but the correction-blocked reason/field-lock
  // only applies when correcting a normal (non-draft) fixed asset.
  const isCorrectionBlocked = everyTrue(
    !!isCorrectionBlockedReasonEnabled,
    isCorrectionMode,
    !isDraftFA,
    someTrue(
      isCorrectionBlockedByEventHistory,
      adjusted,
      isCorrectionBlockedByMonthlyClosing,
      isCorrectionBlockedByAcquisitionMonthlyClosing
    )
  )

  // undefined until the adjust-history check settles, so the reason box never shows a message that is about to flip.
  const [isEventRegisteredAfterAdjusted, setIsEventRegisteredAfterAdjusted] = useState<boolean>()
  const shouldCompareAdjustedWithEvents = everyTrue(isCorrectionBlocked, adjusted, isCorrectionBlockedByEventHistory)
  const isCheckingEventAfterAdjusted = shouldCompareAdjustedWithEvents && isEventRegisteredAfterAdjusted === undefined

  useEffect(() => {
    if (!shouldCompareAdjustedWithEvents) return
    checkIsEventAfterAdjusted(biid).then(setIsEventRegisteredAfterAdjusted)
  }, [biid, shouldCompareAdjustedWithEvents])

  const correctionBlockedReason = everyTrue(isCorrectionBlocked, !isCheckingEventAfterAdjusted)
    ? getCorrectionBlockedReason(
        biid,
        isCorrectionBlockedByEventHistory,
        adjusted,
        isCorrectionBlockedByMonthlyClosing,
        transitionAt,
        isCorrectionBlockedByAcquisitionMonthlyClosing,
        acquiredAt,
        isEventRegisteredAfterAdjusted
      )
    : null

  return {
    correctionBlockedReason,
    isCorrectionBlocked,
    isCorrectionBlockedReasonEnabled: !!isCorrectionBlockedReasonEnabled,
  }
}
